import { isPlatformBrowser } from '@angular/common';
import { httpResource, HttpResourceRef } from '@angular/common/http';
import { effect, inject, Injectable, Injector, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';

import { EcmDispatcherModule, EnvUtils, StoreService } from '@ecm/dispatcher';

import { HttpMethod } from '../enums/http-method.enum';
import { RequestOptions } from '../enums/request-options';
import { Status } from '../enums/status.enum';
import { TransactionInputs } from '../enums/transaction-inputs';
import { RequestUtils } from '../utils/http/request.utils';
import { IdUtils } from '../utils/id.utils';
import { BcoService } from './bco.service';
import { ErrorHandlerService } from './error-handler.service';
import { RandomUrlService } from './random-url.service';
import { UaService } from './ua-service';

/**
 * 🔥 Configuration for mutation resources (POST/PUT/PATCH/DELETE).
 * Set to `undefined` to skip the request (initial state / not ready yet).
 */
export interface MutationRequest<TBody = unknown> {
    url: string;
    body?: TBody;
}

type MutationRequestSource<TBody = unknown> =
    | Signal<MutationRequest<TBody> | undefined>
    | (() => MutationRequest<TBody> | undefined);

/**
 * 📦 ErableHttpResourceService — Signal-based equivalent of ErableHttpService.
 *
 * Uses Angular's `httpResource` (Angular 20+) instead of `HttpClient` for reactive data fetching.
 * Returns `HttpResourceRef<T>` (signals) instead of `Observable<T>`.
 *
 * ## Key architectural differences with ErableHttpService:
 *
 * | Concern               | ErableHttpService (Observable)         | ErableHttpResourceService (Signal)        |
 * |-----------------------|----------------------------------------|-------------------------------------------|
 * | **Return type**       | `Observable<T>`                        | `HttpResourceRef<T>` (signals)            |
 * | **State management**  | Manual (loading, error, value)         | Built-in via `status()`, `isLoading()`    |
 * | **Caching (GET)**     | Custom in-memory cache by URL          | Resource lifecycle (survives until destroyed) |
 * | **Re-fetching**       | Manual (`force` option)                | `.reload()` or request signal change      |
 * | **Mutations**         | Imperative (call method → get Observable) | Reactive (update signal → resource fires) |
 * | **Error handling**    | Observable `error` + `onError` callback | `status() === 'error'`, `error()` signal |
 * | **BCO tracking**      | Inline in request lifecycle            | Via `trackResource()` effect helper       |
 * | **Timeout**           | Per-request via `rxjs/timeout`         | ⚠️ Use HTTP interceptor instead           |
 * | **JWT processing**    | Inline `computeAdvisorJwtHeader`       | ⚠️ Use HTTP interceptor instead           |
 *
 * ## ⚠️ Cross-cutting concerns handled differently
 *
 * `httpResource` uses Angular's `HttpClient` internally, so **HTTP interceptors** are the
 * idiomatic way to handle:
 * - **Timeout**: Create a timeout interceptor using `AbortController` or `rxjs/timeout`
 * - **BCO transaction tracking**: Use `trackResource()` for resource-level tracking,
 *   or create an interceptor for request-level tracking
 * - **JWT advisor header processing**: Create an interceptor that reads response headers
 *
 * ## Usage examples
 *
 * ### 🔥 Reactive GET in a service
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class OrderService {
 *     private readonly _erableResource = inject(ErableHttpResourceService);
 *     private readonly _env = inject(EnvironmentService);
 *
 *     readonly ordersResource = this._erableResource.get<Order[]>(
 *         () => this._env.instant().API.COMMANDES.GET_ORDERS_URL,
 *     );
 * }
 * ```
 *
 * ### 🔥 Reactive GET with dynamic parameters
 * ```typescript
 * // Resource re-fetches automatically when contractId() changes
 * readonly contractId = input.required<string>();
 * readonly contractResource = this._erableResource.get<Contract>(
 *     () => `/api/contracts/${this.contractId()}`,
 * );
 * ```
 *
 * ### 🔥 POST mutation triggered on demand
 * ```typescript
 * private readonly _submitRequest = signal<MutationRequest | undefined>(undefined);
 * readonly submitResource = this._erableResource.post<Response>(
 *     this._submitRequest,
 * );
 *
 * onSubmit(data: FormData): void {
 *     this._submitRequest.set({ url: '/api/submit', body: data });
 * }
 * ```
 *
 * ### 🔥 BCO tracking with trackResource()
 * ```typescript
 * private readonly _tracking = this._erableResource.trackResource(this.ordersResource, {
 *     method: HttpMethod.get,
 *     feature: 'commandes',
 *     functionality: 'list',
 *     service: 'orders',
 * });
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class ErableHttpResourceService {
    private static readonly CLASS_NAME = 'ErableHttpResourceService';
    private static readonly ECLAIR_TIMEOUT_RATIO = 1.5;

    private readonly _injector = inject(Injector);
    private readonly _bcoSvc = inject(BcoService);
    private readonly _uaSvc = inject(UaService);
    private readonly _errorHandlerSvc = inject(ErrorHandlerService);
    private readonly _randomUrlSvc = inject(RandomUrlService);
    private readonly _platformId = inject(PLATFORM_ID);

    /** 🔧 Orange monitoring headers */
    private readonly ORIGIN_ID_HEADER = 'X-Orange-Origin-Id';
    private readonly CALLER_ID_HEADER = 'X-Orange-Caller-Id';
    private readonly DEVICE_TYPE_HEADER = 'X-App-Device-Type';

    private readonly DEFAULT_TIMEOUT = {
        get: 10000,
        post: 10000,
        put: 10000,
        patch: 10000,
        delete: 10000,
    };

    private readonly noCacheResponseTypesList: string[] = ['blob'];
    private readonly _getCache = new Map<string, HttpResourceRef<unknown>>();
    private readonly _trackedResources = new WeakMap<HttpResourceRef<unknown>, WritableSignal<string | null>>();

    constructor() {
        this._initSessionId();
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 GET Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive GET resource.
     * The resource automatically fetches when the URL signal changes.
     * Return `undefined` from the URL function to skip the request.
     *
     * @param url Reactive function returning the URL (or `undefined` to skip)
     * @param options Optional request options (headers, partner for round-robin)
     * @returns `HttpResourceRef<T>` with `.value()`, `.status()`, `.isLoading()`, `.error()`
     */
    public get<T>(url: string, options?: RequestOptions): HttpResourceRef<T>;
    public get<T>(url: () => string | undefined, options?: RequestOptions): HttpResourceRef<T>;
    public get<T>(url: string | (() => string | undefined), options?: RequestOptions): HttpResourceRef<T> {
        if (typeof url === 'string') {
            if (options?.force) {
                this.clear(url);
            }

            const cached = this._getCache.get(url) as HttpResourceRef<T> | undefined;
            if (cached) {
                return cached;
            }

            const resource = this._createGetResource<T>(() => url, options);
            const trackedResource = this._attachTracking(resource, HttpMethod.get, options);
            const responseType = RequestUtils.getResponseType(HttpMethod.get, options);

            if (!this.noCacheResponseTypesList.includes(responseType)) {
                this._getCache.set(url, trackedResource);
            }

            return trackedResource;
        }

        const resource = this._createGetResource<T>(url, options);
        return this._attachTracking(resource, HttpMethod.get, options);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 POST Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive POST resource.
     * The resource fires when the request signal emits a non-undefined value.
     *
     * @param request Reactive function (or signal) returning `{ url, body }` or `undefined`
     * @param options Optional request options
     */
    public post<TResponse, TBody = unknown>(
        url: string,
        body: TBody,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse>;
    public post<TResponse, TBody = unknown>(
        request: MutationRequestSource<TBody>,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse>;
    public post<TResponse, TBody = unknown>(
        requestOrUrl: string | MutationRequestSource<TBody>,
        bodyOrOptions?: TBody | RequestOptions,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        if (typeof requestOrUrl === 'string') {
            const request = signal<MutationRequest<TBody> | undefined>({
                url: requestOrUrl,
                body: bodyOrOptions as TBody,
            });
            const resource = this._createMutationResource<TResponse, TBody>('POST', request, options);
            return this._attachTracking(resource, HttpMethod.post, options);
        }

        const requestOptions = bodyOrOptions as RequestOptions | undefined;
        const resource = this._createMutationResource<TResponse, TBody>('POST', requestOrUrl, requestOptions);

        return this._attachTracking(resource, HttpMethod.post, requestOptions);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 PUT Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive PUT resource.
     *
     * @param request Reactive function (or signal) returning `{ url, body }` or `undefined`
     * @param options Optional request options
     */
    public put<TResponse, TBody = unknown>(
        url: string,
        body: TBody,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse>;
    public put<TResponse, TBody = unknown>(
        request: MutationRequestSource<TBody>,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse>;
    public put<TResponse, TBody = unknown>(
        requestOrUrl: string | MutationRequestSource<TBody>,
        bodyOrOptions?: TBody | RequestOptions,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        if (typeof requestOrUrl === 'string') {
            const request = signal<MutationRequest<TBody> | undefined>({
                url: requestOrUrl,
                body: bodyOrOptions as TBody,
            });
            const resource = this._createMutationResource<TResponse, TBody>('PUT', request, options);
            return this._attachTracking(resource, HttpMethod.put, options);
        }

        const requestOptions = bodyOrOptions as RequestOptions | undefined;
        const resource = this._createMutationResource<TResponse, TBody>('PUT', requestOrUrl, requestOptions);

        return this._attachTracking(resource, HttpMethod.put, requestOptions);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 PATCH Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive PATCH resource.
     *
     * @param request Reactive function (or signal) returning `{ url, body }` or `undefined`
     * @param options Optional request options
     */
    public patch<TResponse, TBody = unknown>(
        url: string,
        body: TBody,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse>;
    public patch<TResponse, TBody = unknown>(
        request: MutationRequestSource<TBody>,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse>;
    public patch<TResponse, TBody = unknown>(
        requestOrUrl: string | MutationRequestSource<TBody>,
        bodyOrOptions?: TBody | RequestOptions,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        if (typeof requestOrUrl === 'string') {
            const request = signal<MutationRequest<TBody> | undefined>({
                url: requestOrUrl,
                body: bodyOrOptions as TBody,
            });
            const resource = this._createMutationResource<TResponse, TBody>('PATCH', request, options);
            return this._attachTracking(resource, HttpMethod.patch, options);
        }

        const requestOptions = bodyOrOptions as RequestOptions | undefined;
        const resource = this._createMutationResource<TResponse, TBody>('PATCH', requestOrUrl, requestOptions);

        return this._attachTracking(resource, HttpMethod.patch, requestOptions);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  📦 DELETE Resource
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates a reactive DELETE resource.
     *
     * @param url Reactive function returning the URL (or `undefined` to skip)
     * @param options Optional request options
     */
    public delete<T>(url: string, options?: RequestOptions): HttpResourceRef<T>;
    public delete<T>(url: () => string | undefined, options?: RequestOptions): HttpResourceRef<T>;
    public delete<T>(url: string | (() => string | undefined), options?: RequestOptions): HttpResourceRef<T> {
        const urlGetter = typeof url === 'string' ? () => url : url;
        const resource = httpResource<T>(
            () => {
                const rawUrl = urlGetter();
                if (!rawUrl) return undefined;

                const { url: processedUrl } = this._processUrl(rawUrl, options?.partner);

                return {
                    url: processedUrl,
                    method: 'DELETE',
                    headers: this._buildHeaders(options),
                    withCredentials: true,
                    reportProgress: false,
                    params: options?.params,
                    timeout: this._getTimeoutForMethod(HttpMethod.delete, options),
                };
            },
            { injector: this._injector },
        ) as HttpResourceRef<T>;

        return this._attachTracking(resource, HttpMethod.delete, options);
    }

    public clear(url: string): boolean {
        return this._getCache.delete(url);
    }

    public clearByPrefix(urlPrefix: string): void {
        for (const key of this._getCache.keys()) {
            if (key.startsWith(urlPrefix)) {
                this.clear(key);
            }
        }
    }

    // ────────────────────────────────────────────────────────────────────────
    //  🔔 BCO Tracking Helper
    // ────────────────────────────────────────────────────────────────────────

    /**
     * Creates an effect that tracks a resource's lifecycle via BCO (begin/end transaction).
     * Place this alongside the resource in your service or component.
     *
     * The effect watches the resource `status()` signal and:
     * - Starts a BCO transaction when the resource begins loading
     * - Ends the transaction with OK when resolved
     * - Ends the transaction with KOSI/KORG/TO when errored
     *
     * @param resource The `HttpResourceRef` to track
     * @param trackingOptions BCO metadata (feature, functionality, service, etc.)
     * @returns A writable signal holding the current transactionId (for external use if needed)
     *
     * @example
     * ```typescript
     * readonly ordersResource = this._erableResource.get<Order[]>(() => url);
     * private readonly _tracking = this._erableResource.trackResource(this.ordersResource, {
     *     method: HttpMethod.get,
     *     feature: 'commandes',
     *     functionality: 'list',
     *     service: 'orders',
     * });
     * ```
     */
    public trackResource<T>(
        resource: HttpResourceRef<T>,
        trackingOptions: {
            method: HttpMethod;
            feature?: string;
            functionality?: string;
            pageId?: string;
            partner?: string;
            service?: string;
        },
    ): WritableSignal<string | null> {
        const existingSignal = this._trackedResources.get(resource);

        if (existingSignal) {
            return existingSignal;
        }

        const transactionId: WritableSignal<string | null> = signal(null);
        this._trackedResources.set(resource, transactionId);

        effect(
            () => {
                const status = resource.status();

                if (status === 'loading' || status === 'reloading') {
                    // 🔥 Begin BCO transaction when resource starts loading
                    const inputs: TransactionInputs = {
                        httpMethod: trackingOptions.method,
                        feature: trackingOptions.feature ?? 'undefined',
                        functionality: trackingOptions.functionality ?? 'undefined',
                        pageId: trackingOptions.pageId ?? 'undefined',
                        partner: trackingOptions.partner ?? 'undefined',
                        service: trackingOptions.service ?? 'undefined',
                        originId: RequestUtils.getOriginId(),
                        consumerId: RequestUtils.getCallerId(),
                        sessionId: StoreService.get('session')?.id ?? '',
                    };

                    transactionId.set(this._bcoSvc.beginTransaction(inputs));
                }

                if (status === 'resolved') {
                    const txId = transactionId();

                    if (txId) {
                        this._bcoSvc.endTransaction(txId, Status.OK, '200', '');
                        transactionId.set(null);
                    }
                }

                if (status === 'error') {
                    const txId = transactionId();
                    const error = resource.error();

                    if (txId) {
                        const { errorStatus, errorCode, errorMessage } = this._classifyError(error);
                        this._bcoSvc.endTransaction(txId, errorStatus, errorCode, errorMessage);
                        transactionId.set(null);
                    }
                }
            },
            { injector: this._injector },
        );

        return transactionId;
    }

    private _attachTracking<T>(
        resource: HttpResourceRef<T>,
        method: HttpMethod,
        options?: RequestOptions,
    ): HttpResourceRef<T> {
        this.trackResource(resource, {
            method,
            feature: options?.feature,
            functionality: options?.functionality,
            pageId: options?.pageId,
            partner: options?.partner,
            service: options?.service,
        });

        return resource;
    }

    // ────────────────────────────────────────────────────────────────────────
    //  🔧 Private helpers
    // ────────────────────────────────────────────────────────────────────────

    private _createGetResource<T>(url: () => string | undefined, options?: RequestOptions): HttpResourceRef<T> {
        return httpResource<T>(
            () => {
                const rawUrl = url();
                if (!rawUrl) return undefined;

                const { url: processedUrl } = this._processUrl(rawUrl, options?.partner);

                return {
                    url: processedUrl,
                    method: 'GET',
                    headers: this._buildHeaders(options),
                    withCredentials: true,
                    reportProgress: false,
                    params: options?.params,
                    timeout: this._getTimeoutForMethod(HttpMethod.get, options),
                };
            },
            { injector: this._injector },
        ) as HttpResourceRef<T>;
    }

    /**
     * 🔧 Creates a mutation resource (POST/PUT/PATCH) with common configuration.
     */
    private _createMutationResource<TResponse, TBody = unknown>(
        method: 'POST' | 'PUT' | 'PATCH',
        request: MutationRequestSource<TBody>,
        options?: RequestOptions,
    ): HttpResourceRef<TResponse> {
        return httpResource<TResponse>(
            () => {
                const req = request();
                if (!req) return undefined;

                const { url: processedUrl } = this._processUrl(req.url, options?.partner);

                return {
                    url: processedUrl,
                    method,
                    body: req.body,
                    headers: this._buildHeaders(options),
                    withCredentials: true,
                    reportProgress: false,
                    params: options?.params,
                    timeout: this._getTimeoutForMethod(this._toHttpMethod(method), options),
                };
            },
            { injector: this._injector },
        ) as HttpResourceRef<TResponse>;
    }

    private _toHttpMethod(method: 'POST' | 'PUT' | 'PATCH'): HttpMethod {
        if (method === 'POST') {
            return HttpMethod.post;
        }

        if (method === 'PUT') {
            return HttpMethod.put;
        }

        return HttpMethod.patch;
    }

    /**
     * 🔧 Builds default headers matching ErableHttpService conventions.
     * Includes Orange monitoring headers + custom headers from options.
     */
    private _buildHeaders(options?: RequestOptions): Record<string, string> {
        const headers: Record<string, string> = {
            [this.ORIGIN_ID_HEADER]: RequestUtils.getOriginId(),
            [this.CALLER_ID_HEADER]: RequestUtils.getCallerId(),
            [this.DEVICE_TYPE_HEADER]: this._uaSvc.getDeviceType(),
            Accept: 'application/json',
            'Cache-Control': 'no-cache, no-store',
            'X-Orange-Session-Id': StoreService.get('session')?.id ?? '',
            'X-Orange-Request-Id': IdUtils.generateUUID(),
        };

        // Add Content-Type only if not explicitly disabled
        if (!options?.disableContentTypeHeader) {
            headers['Content-Type'] = 'application/json';
        }

        // Merge custom headers from options
        if (options?.headers) {
            for (const key of options.headers.keys()) {
                const headerValue = options.headers.get(key);
                if (headerValue !== null) {
                    headers[key] = headerValue;
                }
            }
        }

        return headers;
    }

    /**
     * 🔧 Processes URL through RandomUrlService for round-robin support.
     */
    private _processUrl(url: string, partner?: string): { url: string; partner: string } {
        return this._randomUrlSvc.getUrlAndPartner(url, partner ?? 'undefined');
    }

    /**
     * 🔧 Classifies an HTTP error into BCO-compatible status/code/message.
     * Mirrors the error classification logic from ErableHttpService.onError.
     */
    private _classifyError(error: unknown): { errorStatus: string; errorCode: string; errorMessage: string } {
        // HttpErrorResponse from Angular's HttpClient (used internally by httpResource)
        if (error && typeof error === 'object' && 'status' in error) {
            const httpError = error as { status?: number; name?: string; message?: string; error?: any };
            const isTimeout = httpError.name === 'TimeoutError';

            let errorCode: string | undefined = isTimeout ? 'ECM_TO' : httpError.status?.toString();
            let errorStatus: string;

            if (isTimeout) {
                errorStatus = Status.TO;
            } else if (errorCode === undefined || errorCode === 'undefined') {
                this._errorHandlerSvc.handleError(error as unknown as Error);
                errorCode = `ECM_${httpError.name}`;
                errorStatus = Status.KORG;
            } else {
                errorStatus = errorCode.startsWith('4') ? Status.KORG : Status.KOSI;
            }

            const errorMessage =
                httpError.error?.error?.technicalDescription ??
                httpError.error?.error?.technicalMessage ??
                httpError.message ??
                'undefined';

            return {
                errorStatus,
                errorCode: errorCode ?? 'undefined',
                errorMessage,
            };
        }

        // Fallback for unknown errors
        return {
            errorStatus: Status.KOSI,
            errorCode: 'ECM_UNKNOWN',
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
        };
    }

    private _getTimeoutForMethod(method: HttpMethod, options?: { timeout?: number }): number {
        const timeoutKey = method.toLowerCase() as keyof typeof this.DEFAULT_TIMEOUT;
        const configTimeout = options?.timeout ? options.timeout : this.DEFAULT_TIMEOUT[timeoutKey];
        const ratio = EnvUtils.isEclair() ? ErableHttpResourceService.ECLAIR_TIMEOUT_RATIO : 1;

        return ratio * configTimeout;
    }

    /**
     * 🔧 Initializes session ID in StoreService (same as ErableHttpService).
     */
    private _initSessionId(): void {
        if (isPlatformBrowser(this._platformId)) {
            const sessionId = IdUtils.generateUUID();
            EcmDispatcherModule.sendConsoleInfo(ErableHttpResourceService.CLASS_NAME, 'Storing Session ' + sessionId);
            StoreService.set('session', { id: sessionId });
        }
    }
}
